/**
 * Columns of the A/B verdict table, shared by the Performance run page and the proof page.
 *
 * @module features/perf/perfRowColumns
 */
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { TagBadge, Text } from '@chatsdet/ui';
import type { PerfRow } from '@chatsdet/types/perf';
import { formatDelta, formatInterval, formatValue } from '@chatsdet/types/perf';
import { verdictPresentation } from './perfPresentation';

const formatArm = (arm: PerfRow['base'], unit: PerfRow['unit']) =>
  arm.values.length === 0 ? '-' : formatValue(arm.median, unit);

export const ROW_COLUMNS: ColumnDef<StockFeatures, PerfRow>[] = [
  {
    id: 'metric',
    header: 'Metric',
    enableSorting: false,
    cell: ({ row }) => (
      <Text size="sm" className="font-medium">
        {row.original.label}
        {!row.original.gated && <span className="font-normal text-muted-foreground"> (not gated)</span>}
      </Text>
    ),
    meta: { flex: 3, minWidth: 150 },
  },
  {
    id: 'base',
    header: 'Base',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="tabular-nums">{formatArm(row.original.base, row.original.unit)}</Text>,
    meta: { minWidth: 80 },
  },
  {
    id: 'head',
    header: 'Head',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="tabular-nums">{formatArm(row.original.head, row.original.unit)}</Text>,
    meta: { minWidth: 80 },
  },
  {
    id: 'delta',
    header: 'Delta',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="tabular-nums">{formatDelta(row.original)}</Text>,
    meta: { minWidth: 80 },
  },
  {
    id: 'ci',
    header: '95% CI',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="tabular-nums">{formatInterval(row.original)}</Text>,
    meta: { flex: 2, minWidth: 120 },
  },
  {
    id: 'verdict',
    header: 'Verdict',
    enableSorting: false,
    cell: ({ row }) => {
      const { label, color } = verdictPresentation(row.original.verdict);
      return <TagBadge name={label} color={color} size="md" />;
    },
    meta: { minWidth: 100 },
  },
];
